import React from 'react'
import Accordion from '../Accordion/Accordion'
import PropTypes from 'prop-types'
import * as Styled from './QualityIndicators.styles'
import {
  Indicator,
  IndicatorLegend,
  // IndicatorOverlayContent
} from '../NursingIndicator/partials'
import { useTextContext } from 'hocs'
import { Button } from 'components/atoms'
import { Overlay } from 'components/molecules'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { useSelector } from 'react-redux'
import QualityIndicatorsOverlayContent from './partials/IndicatorOverlayContent/QualityIndicatorsOverlayContent'
import { qualityIndicatorsLabels } from './qualityIndicatorsLabels'

function* generateNPTableEntries() {
  const createIndicator = (criteria, interference) => ({
    classification: -1,
    criteria,
    interference,
  })

  for (let criteria = 1; criteria <= 10; criteria++) {
    const hasInterference =
      criteria === 1 || criteria === 2 || (criteria >= 4 && criteria <= 6)
    const indicators = hasInterference
      ? [createIndicator(criteria, 'a'), createIndicator(criteria, 'b')]
      : [createIndicator(criteria)]

    yield [criteria, indicators]
  }
}

// generate a map of all indicators with a classification of -1 (NP)
const generateNPTable = () => new Map(generateNPTableEntries())

const QualityIndicators = ({
  evaluationDate,
  reportStatus,
  // reportStatus,
  indicators,
  variant = 'circle',
}) => {
  const textContext = useTextContext()
  const [selectedIndicator, setSelectedIndicator] = React.useState({})
  const labels = useSelector((state) => state.detailData.labels)
  const [open, setOpen] = React.useState(false)
  const [remappedIndicators] = React.useState(() => {
    // if the report status equals 3, then the indicators are all -1 (NP)
    if (reportStatus === 3 || reportStatus === 2) return generateNPTable()

    const _remappedIndicators = new Map()

    indicators.forEach((indicator) => {
      const indicatorByCriteria = _remappedIndicators.get(indicator.criteria)
      if (indicatorByCriteria) {
        indicatorByCriteria.push(indicator)
      } else {
        _remappedIndicators.set(indicator.criteria, [indicator])
      }
    })
    return _remappedIndicators
  })

  const handleMoreDetailsClick = (indicator) => {
    setSelectedIndicator({ ...indicator, reportStatus })
    setOpen(true)
  }

  const checkForSpecialCriteria = (key) => {
    if (variant === 'square' && key === '5') {
      return false
    }
    return true
  }

  if (reportStatus === -1 || reportStatus === -2) {
    return (
      <>
        <p className='semi-bold'>Qualitätsindikatoren</p>
        <p>{qualityIndicatorsLabels.reportStatusText[reportStatus]}</p>
      </>
    )
  }

  return (
    <Styled.QualityIndicators>
      <p>
        <b>{textContext[`nursingIndicator.title.${ variant }`]}</b>
      </p>

      <p>{labels?.PE_PH_QUALITY_REPORT_INDICATORS_INTRO}</p>

      <p>{labels?.PE_PH_QUALITY_REPORT_INDICATORS_INFO}</p>

      {evaluationDate && (
        <p>
          {textContext['nursingIndicator.ratingDate.title']}
          <b>
            {dayjs.unix(evaluationDate).locale('de').format('DD. MMMM YYYY')}
          </b>
        </p>
      )}

      {/* type && (
        <p>
          {textContext['nursingIndicator.auditType.title']}
          <b>{type}</b>
        </p>
      )*/}

      <div className='indicator-legend-accordion'>
        <Accordion label='Bedeutung der Symbole' type='UNDERLINED'>
          <IndicatorLegend variant={ variant } />
        </Accordion>
      </div>
      <p>
        <b>{textContext[`nursingIndicator.table.title.${ variant }`]}</b>
      </p>
      <p>{textContext[`nursingIndicator.rating.explanation.${ variant }`]}</p>
      <hr />
      {[...remappedIndicators].map(([key, indicator]) => (
        <React.Fragment key={ key }>
          <div className='indicator-wrapper'>
            <b className='indicator-enumeration'>{key}.</b>
            <div className='indicator-title'>
              <b className='indicator-type-description'>
                {qualityIndicatorsLabels.titles[key]}
              </b>
              {indicator && indicator.length === 1 && (
                <div className='indicator-rating'>
                  <Indicator
                    variant={ variant }
                    rating={ indicator[0].classification }
                  />
                </div>
              )}
              {indicator &&
                (indicator.length === 1 || !checkForSpecialCriteria(key)) && (
                  <Button
                    className='btn-link more-details-button'
                    text='Mehr Details'
                    onClick={ () =>
                      handleMoreDetailsClick({
                        ...indicator[0],
                        title: `${ key }. ${ qualityIndicatorsLabels.titles[key] }`,
                        type: 'single',
                      })
                    }
                  />
                )}
            </div>
          </div>
          {indicator && indicator.length > 1 && checkForSpecialCriteria(key) && (
            <div className='indicator-types'>
              {indicator.map((type, typeIndex) => (
                <div key={ `type-${ typeIndex }` } className='indicator-wrapper'>
                  <b className='indicator-type-char'>{type.interference}.</b>
                  <div className='indicator-type'>
                    <p
                      dangerouslySetInnerHTML={{
                        __html:
                          qualityIndicatorsLabels.interference[key][
                            type.interference
                          ],
                      }}
                    />
                    <div className='indicator-rating'>
                      <Indicator
                        variant={ variant }
                        rating={ type.classification }
                      />
                    </div>
                    <Button
                      className='btn-link more-details-button'
                      text='Mehr Details'
                      onClick={ () =>
                        handleMoreDetailsClick({
                          ...type,
                          title: `${ key }. ${ qualityIndicatorsLabels.titles[key] }`,
                          type: 'multi',
                        })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <hr />
        </React.Fragment>
      ))}

      {/* comment && (
        <>
          <p>
            <b>{textContext['nursingIndicator.comment.title']}</b>
          </p>
          <p>{comment}</p>
        </>
      )*/}

      <Overlay
        id={ 'indicator-overlay ' }
        isActive={ open }
        title={ textContext[`nursingIndicator.overlay.title.${ variant }`] }
        showClose
        onToggleActive={ () => setOpen(false) }>
        <QualityIndicatorsOverlayContent
          indicator={ selectedIndicator }
          variant={ variant }
        />
      </Overlay>
    </Styled.QualityIndicators>
  )
}

QualityIndicators.propTypes = {
  evaluationDate: PropTypes.number,
  reportStatus: PropTypes.number,
  indicators: PropTypes.arrayOf(
    PropTypes.shape({
      classification: PropTypes.number,
      criteria: PropTypes.number,
      interference: PropTypes.string,
      rate: PropTypes.number,
      refValue: PropTypes.number,
      lastResults: PropTypes.arrayOf(
        PropTypes.shape({
          classification: PropTypes.number,
          evaluationDate: PropTypes.number,
          reportStatus: PropTypes.number,
        })
      ),
    })
  ),
  variant: PropTypes.oneOf(['circle', 'square']),
}

export default QualityIndicators
